import type { TeacherInfo } from "../networking/schoolClasses";
import { getAppBaseUrl, getAppDisplayBaseUrl } from "../utils/urlUtils";

type TFunction = (key: string, options?: Record<string, unknown>) => string;

const CLASSROOM_PATH = "klassenzimmer";

/** "Ms Fischer", or just the lastname when the salutation is missing. Empty when unknown. */
export const getTeacherDisplayName = (teacher: TeacherInfo | null): string => {
    if (!teacher?.lastname) return "";
    if (!teacher.salutation) return teacher.lastname;

    return `${teacher.salutation} ${teacher.lastname}`;
};

/** "With Ms Fischer", or "With Fischer" when the salutation is missing. Empty when unknown. */
export const getTeacherSubline = (teacher: TeacherInfo | null, t: TFunction): string => {
    if (!teacher?.lastname) return "";
    if (!teacher.salutation) {
        return t("With {lastname}", {
            _tags: "ClassMemberDashboard, TeacherSubline",
            lastname: teacher.lastname,
        });
    }

    return t("With {salutation} {lastname}", {
        _tags: "ClassMemberDashboard, TeacherSubline",
        salutation: teacher.salutation,
        lastname: teacher.lastname,
    });
};

// Class and login codes are uppercase domain-wide; normalize at the URL boundary so call sites
// can pass raw values (e.g. `member.schoolClassId` from the API) without each having to remember.
const normalizeCode = (code: string): string => code.toUpperCase();

export const getClassroomRoute = (schoolClassId: string): string => {
    return `/${CLASSROOM_PATH}/${normalizeCode(schoolClassId)}`;
};

export const getClassroomRootRoute = (): string => `/${CLASSROOM_PATH}`;

export const getClassroomLoginRoute = (schoolClassId: string): string => {
    return `${getClassroomRoute(schoolClassId)}/login`;
};

// Pairs with getSafeReturnToPath: ClassroomLogin validates on arrival what this encodes.
export const getClassroomLoginRouteWithReturnTo = (schoolClassId: string, returnTo: string): string => {
    return `${getClassroomLoginRoute(schoolClassId)}?returnTo=${encodeURIComponent(returnTo)}`;
};

// URL indicator rather than context state: preview must survive the silent-login redirect and a
// reload, and the classroom subtree has no teacher-side context to hold it. Its counterpart for
// leaving preview is EXIT_PREVIEW_PARAM in navigationHelper.ts.
export const CLASSROOM_PREVIEW_PARAM = "preview";

export const getClassroomPreviewRoute = (schoolClassId: string): string => {
    return `${getClassroomRoute(schoolClassId)}?${CLASSROOM_PREVIEW_PARAM}=1`;
};

export const isClassroomPreviewSearch = (search: string): boolean => {
    return new URLSearchParams(search).get(CLASSROOM_PREVIEW_PARAM) === "1";
};

// Silent-login URL that lands in preview mode: ClassroomLogin's returnTo handling carries the
// preview flag across the login redirect.
export const getClassMemberPreviewLoginRoute = (schoolClassId: string, loginCode: string): string => {
    const returnTo = encodeURIComponent(getClassroomPreviewRoute(schoolClassId));
    return `${getClassroomLoginRoute(schoolClassId)}/${normalizeCode(loginCode)}?returnTo=${returnTo}`;
};

// Same-origin allowlist: must start with a single `/` and contain no scheme. Prevents an attacker
// from crafting `?returnTo=https://evil.example` or `?returnTo=//evil.example` open redirects.
export const getSafeReturnToPath = (returnTo: string | null): string | null => {
    if (!returnTo) return null;
    if (!returnTo.startsWith("/")) return null;
    if (returnTo.startsWith("//")) return null;
    if (returnTo.includes("://")) return null;
    return returnTo;
};

export const getClassroomUrl = (schoolClassId: string): string => {
    return `${getAppBaseUrl()}${getClassroomRoute(schoolClassId)}`;
};

/** Scheme-less variant for on-screen display; copying should still use getClassroomUrl. */
export const getClassroomDisplayUrl = (schoolClassId: string): string => {
    return `${getAppDisplayBaseUrl()}${getClassroomRoute(schoolClassId)}`;
};

export const getClassMemberUrl = (schoolClassId: string, loginCode: string): string => {
    return `${getClassroomUrl(schoolClassId)}/login/${normalizeCode(loginCode)}`;
};

/** Scheme-less variant for on-screen display; copying should still use getClassMemberUrl. */
export const getClassMemberDisplayUrl = (schoolClassId: string, loginCode: string): string => {
    return `${getClassroomDisplayUrl(schoolClassId)}/login/${normalizeCode(loginCode)}`;
};

/** Line-broken for the codes PDF: react-pdf can't wrap an unbroken token. */
export const getClassMemberPdfDisplayUrl = (schoolClassId: string, loginCode: string): string => {
    return `${getAppDisplayBaseUrl()}/\n${CLASSROOM_PATH}/${normalizeCode(schoolClassId)}/\nlogin/${normalizeCode(
        loginCode
    )}`;
};
